import React, { useState } from "react";
import PropTypes from "prop-types";
import AzilisSwitch3, { AzilisSwitch } from "../../components/Switch";
import EditableResultDropdownAutocomplete from "../../components/EditableResultDropdownAutocomplete";
import EditableAutocompleteNonCategory from "../../components/EditableAutocompleteNonCategory";
import EditableResultDropdownSubgroups from "../../components/EditableDropdownSubgroups";
import DeleteIcon from "../../components/SideTableIcons/DeleteIcon";
import SaveIcon from "../../components/SideTableIcons/SaveIcon";
import MailIcon from "../../components/SideTableIcons/MailIcon";
import MultipleCustomColumn from "./components/MultipleCustomColumn";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useUserAccess } from "../../components/WriteAccessContext";
import BuyerInput from "../../components/BuyerInput/BuyerInput";
import ApiAutocompleteDropdown from "../../components/ApiAutocompleteDropdown";
import WarningBox from "../NewCompany/CompanyDetail/components/WarningBox";
import EditableMultiSelectAutocomplete from "../../components/EditableMultiSelectDropdown";

const TableBody = ({
  getTableBodyProps,
  rows,
  prepareRow,
  customInputs,
  dropdownData,
  onInputChange,
  hiddenColumns,
  editedData,
  onSave,
  onDelete,
  setOpen,
  disableRules,
  dynamicPlaceholders,
  renderGroupHeader,
  noShow,
}) => {
  const { userAccess } = useUserAccess();
  const [showWarning, setShowWarning] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [naStates, setNaStates] = useState({});
  const [rowToDelete, setRowToDelete] = useState(null);

  const handleNaClick = (rowIndex, columnId) => {
    const stateKey = `${rowIndex}-${columnId}`;
    setNaStates((prev) => ({
      ...prev,
      [stateKey]: !prev[stateKey],
    }));

    // Clear the input value when NA is enabled
    if (!naStates[stateKey]) {
      onInputChange(rowIndex, columnId, "");
    }
  };

  const groupHeaderStyle = {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    padding: "8px 16px",
  };
  const toggleRowExpanded = (rowId, row) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
    row.toggleRowExpanded();
  };

  const isRowExpanded = (row) => {
    return row.id ? expandedRows[row.id] : false;
  };

  const shouldShowRow = (row) => {
    if (!row.parent) return true; // Show top-level rows

    // Check if all parent rows are expanded
    let currentRow = row.parent;
    while (currentRow) {
      if (!isRowExpanded(currentRow)) return false;
      currentRow = currentRow.parent;
    }
    return true;
  };

  return (
    <tbody {...getTableBodyProps()}>
      {rows.map((row) => {
        prepareRow(row);
        if (!shouldShowRow(row)) return null;

        const handleDelete = () => {
          onDelete(rowToDelete);
          setShowWarning(false);
          setRowToDelete(null);
        };
        if (row.isGrouped) {
          const isExpanded = isRowExpanded(row);
          return (
            <tr
              {...row.getRowProps()}
              style={groupHeaderStyle}
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpanded(row.id, row);
              }}
            >
              <td
                colSpan={row.cells.length}
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                }}
                onClick={() => row.toggleExpanded()}
              >
                <span style={{ marginRight: "8px" }}>
                  {isExpanded ? "👇" : "👉"}
                </span>
                {renderGroupHeader ? renderGroupHeader(row) : row.groupByVal}
              </td>
            </tr>
          );
        }

        // Skip rendering subRows if parent is not expanded
        if (row.isGrouped || (row.parent && !row.parent.isExpanded)) {
          return null;
        }

        return (
          <tr
            {...row.getRowProps()}
            id={row.original.id}
            style={{
              // Add indentation for child rows
              backgroundColor: row.depth > 0 ? "#fafafa" : "white",
            }}
          >
            {row.cells.map((cell) => {
              if (cell.column.type === "spacer") {
                return (
                  <td
                    key={cell.column.id}
                    style={{ width: cell.column.width, padding: 0 }}
                  ></td>
                );
              }
              if (hiddenColumns.includes(cell.column.id)) {
                return (
                  <td
                    key={cell.column.id}
                    style={{
                      width: "30px",
                      padding: "0 5px",
                    }}
                  ></td>
                );
              }
              if (cell.column.id !== "actions") {
                return (
                  <td
                    {...cell.getCellProps()}
                    className={
                      cell.column.type === "spacer"
                        ? "spacer-column"
                        : "td-with-background"
                    }
                  >
                    {cell.isGrouped ? (
                      <>
                        {cell.render("Cell")} ({row.subRows.length})
                      </>
                    ) : cell.isAggregated ? (
                      cell.render("Aggregated")
                    ) : cell.isPlaceholder ? null : (
                      renderEditableCell(
                        cell,
                        (columnId, value) =>
                          onInputChange(row.index, columnId, value),
                        customInputs,
                        dropdownData,
                        editedData[row.index] || {},
                        hiddenColumns,
                        disableRules,
                        dynamicPlaceholders,
                        row,
                        naStates,
                        handleNaClick,
                        noShow
                      )
                    )}
                  </td>
                );
              }
              if (cell.column.id === "actions" && userAccess === "write") {
                return (
                  <td {...cell.getCellProps()}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <>
                        <DeleteIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setRowToDelete(row.original);
                            setShowWarning(true);
                          }}
                        />
                        <SaveIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => onSave(row.index)}
                        />
                      </>
                      <MailIcon
                        style={{ width: "15px", cursor: "pointer" }}
                        onClick={() => {
                          setOpen(true);
                        }}
                      />
                    </div>
                  </td>
                );
              } else if (cell.column.id === "carbon") {
                return (
                  <td {...cell.getCellProps()} style={{ textAlign: "center" }}>
                    {typeof cell.value === "number"
                      ? cell.value.toFixed(4)
                      : cell.value}
                  </td>
                );
              } else {
                return (
                  <td
                    {...cell.getCellProps()}
                    style={{
                      textAlign: "center",
                    }}
                  >
                    {cell.render("Cell")}
                  </td>
                );
              }
            })}
            {showWarning && (
              <WarningBox
                message="Are you sure you want to delete this item?"
                onConfirm={handleDelete}
                onCancel={() => setShowWarning(false)}
                showCancel={true}
              />
            )}
          </tr>
        );
      })}
    </tbody>
  );
};

const renderEditableCell = (
  cell,
  onInputChange,
  customInputs,
  dropdownData,
  rowEditedData,
  hiddenColumns,
  disableRules,
  dynamicPlaceholders,
  row,
  naStates,
  handleNaClick,
  noShow
) => {
  const { column } = cell;
  const stateKey = `${row.index}-${column.id}`;
  const isNaActive = naStates[stateKey];

  const getValue = () => {
    // Special handling for customMulti nested fields
    if (column.type === "multipleCustom") {
      return row.original;
    } else if (column.id?.includes("customMulti_")) {
      // Extract everything after "customMulti_"
      const fieldName = column.id.substring(
        column.id.indexOf("customMulti_") + 12
      );

      if (rowEditedData[`customMulti_${fieldName}`] !== undefined) {
        const editedValue = rowEditedData[`customMulti_${fieldName}`];
        return column.type === "number" && editedValue === "" ? 0 : editedValue;
      } else if (rowEditedData[fieldName] !== undefined) {
        const baseEditedValue = rowEditedData[fieldName];
        return column.type === "number" && baseEditedValue === ""
          ? 0
          : baseEditedValue;
      } else {
        return row.original[fieldName];
      }
    }

    // Default case for other fields
    return rowEditedData[column.id] !== undefined
      ? rowEditedData[column.id]
      : cell.value;
  };

  const value = getValue();

  const isDisabled =
    (disableRules && disableRules[column.id]
      ? disableRules[column.id](rowEditedData, row)
      : false) || isNaActive;

  const isNotShown =
    noShow && column?.id && typeof noShow[column.id] === "function"
      ? noShow[column.id](rowEditedData, row)
      : false;
  const handleMultipleCustomChange = (customInput, newValue) => {
    const fieldId = `customMulti_${customInput.accessor}`;
    const baseFieldId = customInput.accessor;
    if (customInput.type === "number" && newValue === "") {
      onInputChange(fieldId, null);
      onInputChange(baseFieldId, null);
    } else {
      onInputChange(fieldId, newValue);
    }
  };

  const handleDropdownChange = (columnId, newValue) => {
    onInputChange(columnId, newValue);

    const placeholderConfig = dynamicPlaceholders[columnId];
    if (placeholderConfig) {
      const { targetField, getPlaceholder } = placeholderConfig;
      const placeholderValue = getPlaceholder(
        newValue.value,
        dropdownData[columnId],
        rowEditedData,
        row.original.id
      );
      if (placeholderValue !== undefined) {
        onInputChange(targetField, placeholderValue);
      }
    }
  };
  const inputStyle = {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    textAlign: "center",
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? "none" : "auto",
  };

  const nonDropdownInputStyle = {
    ...inputStyle,
    height: "28px",
  };

  const naBtnStyle = {
    marginLeft: "5px",
    color: "white",
    opacity: isNaActive ? 1 : 0.5,
    cursor: "pointer",
    padding: "3px",
  };

  const dropdownInputStyle = {
    ...inputStyle,
    display: "flex",
    alignItems: "center",
  };

  switch (column.type) {
    case "text":
    case "number":
      const isHidden =
        column.isNA &&
        column.isNA === true &&
        Object.keys(naStates).length > 0 &&
        Object.values(naStates)?.[0] === true
          ? true
          : column.isNA === false &&
            Object.keys(naStates).length > 0 &&
            Object.values(naStates)?.[0] === false
          ? true
          : false;
      return (
        !isHidden && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <input
                  type={column.type}
                  value={value || ""}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (column.type === "number") {
                      // Allow empty string or valid decimal inputs
                      if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                        // Convert to number only when needed for validation
                        const numValue = value === "" ? "" : Number(value);

                        if (value !== "" && !isNaN(numValue)) {
                          // Apply min/max constraints after conversion
                          if (column.minValue !== undefined) {
                            value = Math.max(
                              column.minValue,
                              numValue
                            ).toString();
                          }
                          if (column.maxValue !== undefined) {
                            value = Math.min(
                              column.maxValue,
                              numValue
                            ).toString();
                          }
                        }

                        onInputChange(column.id, value);
                      }
                    } else {
                      onInputChange(column.id, value);
                    }
                  }}
                  style={{
                    ...nonDropdownInputStyle,
                    width: column.isNABtn ? "calc(100% - 5px)" : column.width,
                    marginRight: column.isNABtn ? "5px" : "0",
                  }}
                  disabled={isDisabled}
                />
                <>
                  {column.unit && typeof column.unit === "function" ? (
                    <span style={{ marginLeft: "5px", color: "#193666" }}>
                      {column.unit(row.original.id)}
                    </span>
                  ) : column.unit ? (
                    <span style={{ marginLeft: "5px", color: "#193666" }}>
                      {column.unit}
                    </span>
                  ) : null}
                </>
              </div>
              <>
                {column.isNABtn ? (
                  <span
                    className="na-btn-input"
                    style={naBtnStyle}
                    onClick={() => handleNaClick(row.index, column.id)}
                  >
                    n/a
                  </span>
                ) : null}
              </>
            </div>
          </>
        )
      );
    case "dropdown":
      if (isNotShown === true) return;
      const options = dropdownData[column.id] || [];
      if (column.isMultiSelect) {
        return (
          <div
            style={{
              ...dropdownInputStyle,
              display: "flex",
              alignItems: "center",
            }}
          >
            <EditableMultiSelectAutocomplete
              chosen={value}
              setValue={(newValue) => handleDropdownChange(column.id, newValue)}
              options={options.map((content) => ({
                title: content.name,
                value: content.value,
                name: content.name,
              }))}
              edit={true}
              disabled={isDisabled}
            />
          </div>
        );
      }
      if (column.isSubgroup) {
        return (
          <div
            style={{
              ...dropdownInputStyle,

              display: "flex",
              alignItems: "center",
            }}
          >
            <EditableResultDropdownSubgroups
              chosen={value}
              setValue={(newValue) => handleDropdownChange(column.id, newValue)}
              options={options.map((content) => ({
                subgroup: content.subgroup,
                subgroup_en: content.subgroup_en,
                title: content.name,
                value: content.value,
                name: content.name,
              }))}
              edit={true}
              style={dropdownInputStyle}
              check={"noProperty"}
              disabled={isDisabled}
            />
          </div>
        );
      } else if (column.isCategory) {
        return (
          <div
            style={{
              ...dropdownInputStyle,

              display: "flex",
              alignItems: "center",
            }}
          >
            <EditableResultDropdownAutocomplete
              chosen={value}
              setValue={(newValue) => handleDropdownChange(column.id, newValue)}
              options={options.map((content) => ({
                category: content.category,
                title: content.name,
                value: content.value,
                name: content.name,
              }))}
              edit={true}
              style={dropdownInputStyle}
              check={"noProperty"}
              disabled={isDisabled}
            />
          </div>
        );
      } else {
        return (
          <div
            style={{
              ...dropdownInputStyle,
              display: "flex",
              alignItems: "center",
            }}
          >
            <EditableAutocompleteNonCategory
              chosen={value}
              setValue={(newValue) => handleDropdownChange(column.id, newValue)}
              options={options.map((content) => ({
                title: content.name,
                value: content.value,
                name: content.name,
              }))}
              edit={true}
              style={dropdownInputStyle}
              check={"noProperty"}
              disabled={isDisabled}
            />
          </div>
        );
      }
    case "switch":
      return (
        <AzilisSwitch
          key={hiddenColumns.join(",")}
          choice1={column.choice1 || "Yes"}
          choice2={column.choice2 || "No"}
          which={value}
          setWhich={(newValue) => onInputChange(column.id, newValue)}
          styleNo={column.styleNo || 6}
          disabled={isDisabled}
        />
      );
    case "switch3":
      return (
        <AzilisSwitch3
          key={hiddenColumns.join(",")}
          choice1={column.choice1 || "Yes"}
          choice2={column.choice2 || "No"}
          choice3={column.choice3}
          value1={column.choice1 || "Yes"}
          value2={column.choice2 || "No"}
          value3={column.choice3}
          which={value}
          setWhich={(newValue) => onInputChange(column.id, newValue)}
          styleNo={column.styleNo || 6}
          disabled={isDisabled}
        />
      );
    case "custom":
      const CustomInput = customInputs[column.id];
      return (
        <CustomInput
          value={value}
          onChange={(newValue) => onInputChange(column.id, newValue)}
          style={nonDropdownInputStyle}
        />
      );
    case "buyerInput":
      return (
        <BuyerInput
          buyerOptions={column.buyerOptions}
          value={value ?? ""}
          onChange={(newValue) => onInputChange(column.id, newValue)}
          disabled={isDisabled}
        />
      );
    case "multipleCustom":
      return (
        <MultipleCustomColumn
          customInputs={column.customInputs}
          naStates={naStates}
          renderInput={(customInput) => {
            // Create modified cell for each custom input
            const fieldId = `customMulti_${customInput.accessor}`;
            const fieldValue =
              rowEditedData[fieldId] !== undefined
                ? rowEditedData[fieldId]
                : row.original[customInput.accessor];

            const modifiedCell = {
              ...cell,
              value: fieldValue,
              column: {
                ...customInput,
                id: fieldId,
                type: customInput.type || "number",
                value: fieldValue,
              },
            };

            return renderEditableCell(
              modifiedCell,
              (_, newValue) =>
                handleMultipleCustomChange(customInput, newValue),
              customInputs,
              dropdownData,
              rowEditedData,
              hiddenColumns,
              disableRules,
              dynamicPlaceholders,
              row,
              naStates,
              handleNaClick,
              noShow
            );
          }}
          layout={column.layout}
        />
      );
    case "checkbox":
      return (
        <FormControlLabel
          control={<Checkbox color="success" />}
          checked={value === true}
          onChange={(e) => onInputChange(column.id, e.target.checked)}
          sx={{ marginRight: 0, marginLeft: "-3px" }}
          disabled={isDisabled}
        />
      );
    case "apiAutocomplete":
      return (
        <div
          style={{
            ...dropdownInputStyle,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ApiAutocompleteDropdown
            chosen={value}
            setValue={(newValue) => onInputChange(column.id, newValue)}
            disabled={isDisabled}
            edit={true}
          />
        </div>
      );
    default:
      return value;
  }
};

TableBody.propTypes = {
  getTableBodyProps: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  prepareRow: PropTypes.func.isRequired,
  customInputs: PropTypes.objectOf(PropTypes.func),
  dropdownData: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  onInputChange: PropTypes.func.isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  editingRow: PropTypes.number,
  editedData: PropTypes.object.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
  renderGroupHeader: PropTypes.func,
};

export default TableBody;
